import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Tag, LogOut, Plus, ArrowLeft, Camera, CheckCircle2, X, FileText, Image as ImageIcon, ThumbsUp, ThumbsDown } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const papi = axios.create({ baseURL: API });
const fmtEur = (v) => new Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(Number(v) || 0);
papi.interceptors.request.use((config) => {
  const token = localStorage.getItem("lf_portal_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const PT = {
  de: {
    title: "Encasa Kundenportal", sub: "Reklamationen einreichen & Status verfolgen",
    email: "E-Mail", password: "Passwort", login: "Anmelden", logout: "Abmelden",
    my_complaints: "Meine Reklamationen", new_complaint: "Neue Reklamation",
    defect_category: "Fehlerkategorie", severity: "Schweregrad", order_number: "Auftragsnr.",
    claimed_qty: "Reklamierte Menge", claimed_value: "Reklamationswert (€)", description: "Beschreibung",
    photos: "Fotos anhängen", submit: "Reklamation einreichen", back: "Zurück",
    submitted: "Reklamation eingereicht", status: "Status", received: "Eingegangen am",
    no_complaints: "Noch keine Reklamationen vorhanden.", details: "Details", closure: "Abschluss",
    login_failed: "Anmeldung fehlgeschlagen",
    tab_complaints: "Reklamationen", tab_quotes: "Angebote", tab_artworks: "Freigaben",
    total: "Summe", valid_until: "Gültig bis", view_pdf: "PDF ansehen", version: "Version",
    awaiting: "Wartet auf Ihre Entscheidung", approve: "Freigeben", reject: "Überarbeitung anfordern",
    note_ph: "Anmerkung (optional)", decision_saved: "Entscheidung gespeichert",
    no_quotes: "Noch keine Angebote verfügbar.", no_artworks: "Keine Artworks zur Prüfung.",
  },
  en: {
    title: "Encasa Customer Portal", sub: "Submit complaints & track status",
    email: "E-mail", password: "Password", login: "Sign in", logout: "Sign out",
    my_complaints: "My complaints", new_complaint: "New complaint",
    defect_category: "Defect category", severity: "Severity", order_number: "Order no.",
    claimed_qty: "Claimed quantity", claimed_value: "Claimed value (€)", description: "Description",
    photos: "Attach photos", submit: "Submit complaint", back: "Back",
    submitted: "Complaint submitted", status: "Status", received: "Received",
    no_complaints: "No complaints yet.", details: "Details", closure: "Closure",
    login_failed: "Login failed",
    tab_complaints: "Complaints", tab_quotes: "Quotes", tab_artworks: "Approvals",
    total: "Total", valid_until: "Valid until", view_pdf: "View PDF", version: "Version",
    awaiting: "Awaiting your decision", approve: "Approve", reject: "Request revision",
    note_ph: "Note (optional)", decision_saved: "Decision saved",
    no_quotes: "No quotes available yet.", no_artworks: "No artworks to review.",
  },
};

const STATUS_DE = {
  New: "Neu", Acknowledged: "Bestätigt", "Under Investigation": "In Prüfung",
  "Root Cause Identified": "Ursache ermittelt", "Corrective Action": "Korrekturmaßnahme",
  "8D Report Sent": "8D-Report versendet", "Closed (Accepted)": "Abgeschlossen (anerkannt)",
  "Closed (Rejected)": "Abgeschlossen (abgelehnt)",
};

const STATUS_COLORS = {
  New: "bg-blue-50 text-blue-700", Acknowledged: "bg-indigo-50 text-indigo-700",
  "Under Investigation": "bg-amber-50 text-amber-700", "Root Cause Identified": "bg-amber-50 text-amber-700",
  "Corrective Action": "bg-orange-50 text-orange-700", "8D Report Sent": "bg-purple-50 text-purple-700",
  "Closed (Accepted)": "bg-emerald-100 text-emerald-800", "Closed (Rejected)": "bg-red-50 text-red-600",
};

export default function PortalApp() {
  const [lang, setLang] = useState(localStorage.getItem("lf_lang") || "de");
  const t = (k) => PT[lang][k] || k;
  const [user, setUser] = useState(null);
  const [checking, setChecking] = useState(true);
  const [complaints, setComplaints] = useState([]);
  const [quotes, setQuotes] = useState([]);
  const [artworks, setArtworks] = useState([]);
  const [tab, setTab] = useState("complaints");
  const [awNote, setAwNote] = useState({});
  const [view, setView] = useState("list");
  const [detail, setDetail] = useState(null);
  const [photos, setPhotos] = useState({});
  const [cfg, setCfg] = useState({ defect_categories: [], severities: [] });
  const [loginForm, setLoginForm] = useState({ email: "", password: "" });
  const [form, setForm] = useState({});
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    papi.get("/portal/complaints").then((r) => setComplaints(r.data)).catch(() => {});
    papi.get("/portal/config").then((r) => setCfg(r.data)).catch(() => {});
    papi.get("/portal/quotes").then((r) => setQuotes(r.data)).catch(() => {});
    papi.get("/portal/artworks").then((r) => setArtworks(r.data)).catch(() => {});
  }, []);

  // Resolve a picklist option id to its localized label (portal supports de/en).
  const pl = (key, id) => {
    const opt = (cfg[key] || []).find((o) => o.id === id);
    if (!opt) return id;
    return opt.labels?.[lang] || opt.labels?.en || opt.id;
  };

  useEffect(() => {
    papi.get("/portal/me").then((r) => { setUser(r.data); }).catch(() => setUser(false)).finally(() => setChecking(false));
  }, []);
  useEffect(() => { if (user) load(); }, [user, load]);

  const doLogin = async () => {
    try {
      const r = await axios.post(`${API}/portal/login`, loginForm);
      localStorage.setItem("lf_portal_token", r.data.access_token);
      setUser(r.data.user);
    } catch { toast.error(t("login_failed")); }
  };

  const logout = () => { localStorage.removeItem("lf_portal_token"); setUser(false); };

  const decide = async (aw, decision) => {
    try {
      await papi.post(`/portal/artworks/${aw.id}/decision`, { decision, note: awNote[aw.id] || "" });
      toast.success(t("decision_saved"));
      load();
    } catch { toast.error("Error"); }
  };

  const viewQuotePdf = async (q) => {
    try {
      const r = await papi.get(`/portal/quotes/${q.id}/pdf`, { responseType: "blob" });
      window.open(URL.createObjectURL(r.data));
    } catch { toast.error("Error"); }
  };

  const openDetail = async (c) => {
    setDetail(c);
    try {
      const r = await papi.get(`/portal/complaints/${c.id}/photos`);
      setPhotos((p) => ({ ...p, [c.id]: r.data }));
    } catch {}
  };

  const submit = async () => {
    setSaving(true);
    try {
      const r = await papi.post("/portal/complaints", form);
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        await papi.post(`/portal/complaints/${r.data.id}/photos`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      }
      toast.success(`${t("submitted")}: ${r.data.complaint_number}`);
      setForm({}); setFiles([]); setView("list"); load();
    } catch { toast.error("Error"); } finally { setSaving(false); }
  };

  const LangToggle = () => (
    <div className="flex items-center rounded-full border border-gray-200 overflow-hidden text-xs font-semibold bg-white">
      {["de", "en", "es", "pt", "it"].map((l) => (
        <button key={l} data-testid={`portal-lang-${l}`} onClick={() => { setLang(l); localStorage.setItem("lf_lang", l); }}
          className={`px-2 py-1.5 ${lang === l ? "bg-[#1B2A4A] text-white" : "text-gray-500"}`}>{l.toUpperCase()}</button>
      ))}
    </div>
  );

  if (checking) return <div className="min-h-screen bg-[#1B2A4A] flex items-center justify-center"><div className="h-8 w-8 rounded-full border-2 border-white/30 border-t-[#F5A623] animate-spin" /></div>;

  if (!user) {
    return (
      <div className="min-h-screen bg-[#1B2A4A] flex items-center justify-center p-4" data-testid="portal-login-page">
        <div className="bg-white rounded-xl shadow-2xl p-8 w-full max-w-sm space-y-5">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded bg-[#F5A623] flex items-center justify-center"><Tag className="h-5 w-5 text-[#1B2A4A]" strokeWidth={2.5} /></div>
            <div>
              <p className="font-bold leading-none" style={{ fontFamily: "Work Sans" }}>{t("title")}</p>
              <p className="text-xs text-gray-400 mt-0.5">{t("sub")}</p>
            </div>
          </div>
          <div className="space-y-3">
            <div className="space-y-1"><Label className="text-xs text-gray-500">{t("email")}</Label>
              <Input data-testid="portal-email-input" type="email" value={loginForm.email} onChange={(e) => setLoginForm((f) => ({ ...f, email: e.target.value }))} /></div>
            <div className="space-y-1"><Label className="text-xs text-gray-500">{t("password")}</Label>
              <Input data-testid="portal-password-input" type="password" value={loginForm.password} onChange={(e) => setLoginForm((f) => ({ ...f, password: e.target.value }))}
                onKeyDown={(e) => e.key === "Enter" && doLogin()} /></div>
            <Button className="w-full bg-[#1B2A4A] hover:bg-[#2a3d63] text-white" onClick={doLogin} data-testid="portal-login-btn">{t("login")}</Button>
          </div>
          <div className="flex justify-center"><LangToggle /></div>
        </div>
      </div>
    );
  }

  const token = localStorage.getItem("lf_portal_token");

  return (
    <div className="min-h-screen bg-[#F4F5F7]" data-testid="portal-dashboard">
      <header className="bg-[#1B2A4A] text-white px-4 sm:px-8 py-4 flex items-center gap-3">
        <div className="h-8 w-8 rounded bg-[#F5A623] flex items-center justify-center"><Tag className="h-4 w-4 text-[#1B2A4A]" strokeWidth={2.5} /></div>
        <div className="flex-1 min-w-0">
          <p className="font-bold leading-none truncate" style={{ fontFamily: "Work Sans" }}>{t("title")}</p>
          <p className="text-xs text-white/50 truncate">{user.account_name} · {user.contact_name}</p>
        </div>
        <LangToggle />
        <button onClick={logout} className="p-2 rounded hover:bg-white/10" data-testid="portal-logout-btn"><LogOut className="h-4 w-4" /></button>
      </header>

      <main className="max-w-3xl mx-auto p-4 sm:p-8 space-y-5">
        {view === "list" && (<>
          <div className="flex items-center justify-between">
            <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: "Work Sans" }}>
              {tab === "complaints" ? t("my_complaints") : tab === "quotes" ? t("tab_quotes") : t("tab_artworks")}
            </h1>
            {tab === "complaints" && (
              <Button data-testid="portal-new-complaint-btn" onClick={() => setView("new")}
                className="bg-[#F5A623] hover:bg-[#e0961a] text-[#1B2A4A] font-semibold rounded-full">
                <Plus className="h-4 w-4 mr-1" strokeWidth={2.5} />{t("new_complaint")}
              </Button>
            )}
          </div>
          <div className="flex gap-1 bg-white border border-gray-200 rounded-full p-1 w-fit" data-testid="portal-tabs">
            {[["complaints", t("tab_complaints")], ["quotes", t("tab_quotes")], ["artworks", t("tab_artworks")]].map(([k, lbl]) => (
              <button key={k} data-testid={`portal-tab-${k}`} onClick={() => setTab(k)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${tab === k ? "bg-[#1B2A4A] text-white" : "text-gray-500 hover:bg-gray-100"}`}>
                {lbl}
              </button>
            ))}
          </div>

          {tab === "complaints" && (<>
          {complaints.length === 0 && <p className="text-gray-400 text-sm">{t("no_complaints")}</p>}
          <div className="space-y-3">
            {complaints.map((c) => (
              <button key={c.id} onClick={() => openDetail(c)} data-testid="portal-complaint-card"
                className="w-full text-left bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow duration-150">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="font-mono font-semibold text-[#1B2A4A]">{c.complaint_number}</span>
                  <Badge className={`${STATUS_COLORS[c.status] || "bg-slate-100 text-slate-600"} hover:bg-inherit`} data-testid="portal-status-badge">
                    {lang === "de" ? STATUS_DE[c.status] || c.status : c.status}
                  </Badge>
                </div>
                <p className="text-sm text-gray-600 mt-1">{pl("defect_categories", c.defect_category)} · {pl("severities", c.severity)}</p>
                <p className="text-xs text-gray-400 mt-0.5">{t("received")}: {new Date(c.date_received).toLocaleDateString("de-DE")} {c.order_number && `· ${c.order_number}`}</p>
              </button>
            ))}
          </div>
          </>)}

          {tab === "quotes" && (<>
            {quotes.length === 0 && <p className="text-gray-400 text-sm">{t("no_quotes")}</p>}
            <div className="space-y-3">
              {quotes.map((q) => (
                <div key={q.id} data-testid="portal-quote-card" className="bg-white border border-gray-200 rounded-lg p-4">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="font-mono font-semibold text-[#1B2A4A]">{q.quote_number}</span>
                    <Badge className="bg-slate-100 text-slate-600 hover:bg-slate-100">{q.status}</Badge>
                  </div>
                  <div className="flex items-center justify-between mt-2">
                    <div className="text-sm text-gray-600">
                      <span className="font-mono font-semibold text-gray-900">{fmtEur(q.total)}</span>
                      {q.validity_date && <span className="text-xs text-gray-400 ml-2">{t("valid_until")}: {new Date(q.validity_date).toLocaleDateString("de-DE")}</span>}
                    </div>
                    <Button variant="outline" size="sm" onClick={() => viewQuotePdf(q)} data-testid="portal-quote-pdf-btn">
                      <FileText className="h-3.5 w-3.5 mr-1.5" />{t("view_pdf")}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </>)}

          {tab === "artworks" && (<>
            {artworks.length === 0 && <p className="text-gray-400 text-sm">{t("no_artworks")}</p>}
            <div className="space-y-3">
              {artworks.map((aw) => {
                const open = ["Sent to Customer", "Draft", "Revision Requested"].includes(aw.status);
                return (
                  <div key={aw.id} data-testid="portal-artwork-card" className="bg-white border border-gray-200 rounded-lg p-4">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <span className="font-semibold text-[#1B2A4A] flex items-center gap-2"><ImageIcon className="h-4 w-4 text-gray-400" />{aw.artwork_number} · {aw.name}</span>
                      <Badge className={aw.status === "Approved" ? "bg-emerald-100 text-emerald-700 hover:bg-emerald-100" : aw.status === "Revision Requested" ? "bg-red-100 text-red-700 hover:bg-red-100" : "bg-amber-100 text-amber-700 hover:bg-amber-100"} data-testid="portal-artwork-status">
                        {aw.status}
                      </Badge>
                    </div>
                    {aw.customer_note && <p className="text-xs text-gray-500 mt-1 italic">"{aw.customer_note}"</p>}
                    {open && (
                      <div className="mt-3 space-y-2" data-testid="portal-artwork-decision">
                        <p className="text-xs font-medium text-amber-700">{t("awaiting")}</p>
                        <Textarea value={awNote[aw.id] || ""} onChange={(e) => setAwNote((s) => ({ ...s, [aw.id]: e.target.value }))}
                          placeholder={t("note_ph")} className="text-sm bg-white" rows={2} data-testid="portal-artwork-note" />
                        <div className="flex gap-2">
                          <Button size="sm" onClick={() => decide(aw, "approve")} data-testid="portal-artwork-approve-btn"
                            className="bg-emerald-600 hover:bg-emerald-700 text-white">
                            <ThumbsUp className="h-3.5 w-3.5 mr-1.5" />{t("approve")}
                          </Button>
                          <Button size="sm" variant="outline" onClick={() => decide(aw, "reject")} data-testid="portal-artwork-reject-btn"
                            className="text-red-600 border-red-200 hover:bg-red-50">
                            <ThumbsDown className="h-3.5 w-3.5 mr-1.5" />{t("reject")}
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>)}
        </>)}

        {view === "new" && (
          <div className="space-y-4" data-testid="portal-new-complaint-form">
            <button onClick={() => setView("list")} className="text-sm text-gray-500 flex items-center gap-1"><ArrowLeft className="h-4 w-4" />{t("back")}</button>
            <h1 className="text-2xl font-bold tracking-tight" style={{ fontFamily: "Work Sans" }}>{t("new_complaint")}</h1>
            <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-4">
              <div className="space-y-1">
                <Label className="text-xs text-gray-500">{t("defect_category")} *</Label>
                <Select value={form.defect_category || ""} onValueChange={(v) => setForm((f) => ({ ...f, defect_category: v }))}>
                  <SelectTrigger data-testid="portal-defect-select"><SelectValue placeholder="–" /></SelectTrigger>
                  <SelectContent>{cfg.defect_categories.map((d) => <SelectItem key={d.id} value={d.id}>{pl("defect_categories", d.id)}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-xs text-gray-500">{t("severity")}</Label>
                  <Select value={form.severity || ""} onValueChange={(v) => setForm((f) => ({ ...f, severity: v }))}>
                    <SelectTrigger data-testid="portal-severity-select"><SelectValue placeholder="–" /></SelectTrigger>
                    <SelectContent>{cfg.severities.map((s) => <SelectItem key={s.id} value={s.id}>{pl("severities", s.id)}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-gray-500">{t("order_number")}</Label>
                  <Input data-testid="portal-order-input" value={form.order_number || ""} onChange={(e) => setForm((f) => ({ ...f, order_number: e.target.value }))} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-gray-500">{t("claimed_qty")}</Label>
                  <Input data-testid="portal-qty-input" type="number" value={form.claimed_quantity || ""} onChange={(e) => setForm((f) => ({ ...f, claimed_quantity: e.target.value }))} />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-gray-500">{t("claimed_value")}</Label>
                  <Input data-testid="portal-value-input" type="number" value={form.claimed_value || ""} onChange={(e) => setForm((f) => ({ ...f, claimed_value: e.target.value }))} />
                </div>
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-gray-500">{t("description")}</Label>
                <Textarea data-testid="portal-description-input" rows={4} value={form.description || ""} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} />
              </div>
              <div className="space-y-2">
                <Label className="text-xs text-gray-500 flex items-center gap-1.5"><Camera className="h-3.5 w-3.5" />{t("photos")}</Label>
                <input data-testid="portal-photo-input" type="file" accept="image/*,application/pdf" multiple
                  className="text-sm" onChange={(e) => setFiles(Array.from(e.target.files || []))} />
                {files.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {files.map((f, i) => (
                      <span key={i} className="text-xs bg-slate-100 rounded-full px-2.5 py-1 flex items-center gap-1">
                        {f.name}
                        <button onClick={() => setFiles((fs) => fs.filter((_, j) => j !== i))}><X className="h-3 w-3" /></button>
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <Button className="w-full bg-[#1B2A4A] hover:bg-[#2a3d63] text-white" disabled={!form.defect_category || saving}
                onClick={submit} data-testid="portal-submit-btn">
                {saving ? "…" : t("submit")}
              </Button>
            </div>
          </div>
        )}
      </main>

      {detail && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setDetail(null)}>
          <div className="bg-white rounded-xl max-w-lg w-full p-6 space-y-4 max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="portal-detail-modal">
            <div className="flex items-center justify-between">
              <h2 className="font-mono font-bold text-lg text-[#1B2A4A]">{detail.complaint_number}</h2>
              <button onClick={() => setDetail(null)} data-testid="portal-detail-close"><X className="h-5 w-5 text-gray-400" /></button>
            </div>
            <Badge className={`${STATUS_COLORS[detail.status] || ""} hover:bg-inherit`}>
              {lang === "de" ? STATUS_DE[detail.status] || detail.status : detail.status}
            </Badge>
            <div className="text-sm space-y-1.5">
              <p><span className="text-gray-500">{t("defect_category")}:</span> {pl("defect_categories", detail.defect_category)}</p>
              <p><span className="text-gray-500">{t("severity")}:</span> {pl("severities", detail.severity)}</p>
              {detail.order_number && <p><span className="text-gray-500">{t("order_number")}:</span> {detail.order_number}</p>}
              {detail.description && <p><span className="text-gray-500">{t("description")}:</span> {detail.description}</p>}
              {detail.d8_closure && (
                <p className="flex items-start gap-1.5 text-emerald-700 bg-emerald-50 rounded-md p-2 mt-2">
                  <CheckCircle2 className="h-4 w-4 shrink-0 mt-0.5" />{detail.d8_closure}
                </p>
              )}
            </div>
            {(photos[detail.id] || []).length > 0 && (
              <div className="grid grid-cols-3 gap-2">
                {photos[detail.id].map((f) => (
                  f.content_type?.startsWith("image/") ? (
                    <img key={f.id} src={`${API}/files/download/${f.id}?auth=${token}`} alt={f.original_filename}
                      className="rounded-md border border-gray-200 object-cover h-24 w-full" />
                  ) : (
                    <a key={f.id} href={`${API}/files/download/${f.id}?auth=${token}`} target="_blank" rel="noreferrer"
                      className="text-xs text-blue-600 underline flex items-center h-24 justify-center border rounded-md">{f.original_filename}</a>
                  )
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
